import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DateTimeDisplay } from '@/components/ui/datetime-display';
import { useParams, useNavigate } from 'react-router';

export const CouponDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // TODO: Replace with actual API call to fetch voucher details
  const mockVoucher = {
    id: id || '1',
    title: 'Restaurant Sunshine - 20% Rabatt',
    description: 'Genießen Sie 20% Rabatt auf Ihre gesamte Rechnung im Restaurant Sunshine.',
    longDescription: `
      Erleben Sie kulinarische Vielfalt im Restaurant Sunshine und sparen Sie dabei!

      Dieser Gutschein berechtigt Sie zu 20% Rabatt auf Ihre gesamte Rechnung.
      Gültig für alle Speisen und Getränke aus der regulären Karte.

      Besonderheiten:
      • Nicht kombinierbar mit anderen Aktionen
      • Gilt nicht für bereits reduzierte Angebote
      • Reservierung wird empfohlen
      • Gutschein muss beim Bezahlen vorgezeigt werden
    `,
    provider: {
      name: 'Restaurant Sunshine',
      address: 'Musterstraße 123, 52349 Düren',
      phone: '+49 2421 123456',
      website: 'www.restaurant-sunshine.de',
    },
    validFrom: '2024-01-01T00:00:00Z',
    validUntil: '2024-12-31T23:59:59Z',
    discount: '20%',
    category: 'Gastronomie',
    terms: 'Nicht mit anderen Aktionen kombinierbar. Reservierung empfohlen.',
    isAvailable: true,
  };

  const handleAddToMyVouchers = () => {
    // TODO: Implement API call to add voucher to user's collection
    // Show success message
    // Potentially navigate to "Meine Gutscheine"
  };

  return (
    <div className='container mx-auto px-4 py-8'>
      <div className='max-w-4xl mx-auto'>
        {/* Back Button */}
        <Button variant='ghost' onClick={() => navigate('/coupons')} className='mb-6'>
          ← Zurück zu den Gutscheinen
        </Button>

        {/* Main Content */}
        <div className='grid lg:grid-cols-3 gap-8'>
          {/* Left Column - Details */}
          <div className='lg:col-span-2 space-y-6'>
            <Card>
              <CardHeader>
                <div className='flex justify-between items-start'>
                  <div>
                    <CardTitle className='text-2xl text-cyan-700'>{mockVoucher.title}</CardTitle>
                    <CardDescription className='text-lg mt-2'>{mockVoucher.description}</CardDescription>
                  </div>
                  <Badge variant='secondary' className='bg-lime-100 text-lime-800'>
                    {mockVoucher.discount}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className='whitespace-pre-line text-gray-700'>{mockVoucher.longDescription}</div>
              </CardContent>
            </Card>

            {/* Terms and Conditions */}
            <Card>
              <CardHeader>
                <CardTitle>Bedingungen</CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-gray-700'>{mockVoucher.terms}</p>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Actions & Info */}
          <div className='space-y-6'>
            {/* Action Card */}
            <Card>
              <CardHeader>
                <CardTitle>Gutschein sichern</CardTitle>
                <CardDescription>Fügen Sie diesen Gutschein zu Ihrer Sammlung hinzu</CardDescription>
              </CardHeader>
              <CardContent className='space-y-4'>
                <Button
                  onClick={handleAddToMyVouchers}
                  className='w-full bg-cyan-600 hover:bg-cyan-700'
                  disabled={!mockVoucher.isAvailable}
                >
                  {mockVoucher.isAvailable ? 'Zu meinen Gutscheinen hinzufügen' : 'Nicht verfügbar'}
                </Button>

                <div className='text-sm text-gray-500 text-center'>Kostenlos • Sofort verfügbar</div>
              </CardContent>
            </Card>

            {/* Validity Info */}
            <Card>
              <CardHeader>
                <CardTitle>Gültigkeit</CardTitle>
              </CardHeader>
              <CardContent className='space-y-2'>
                <div className='flex justify-between'>
                  <span className='text-gray-600'>Gültig ab:</span>
                  <DateTimeDisplay date={mockVoucher.validFrom} format='date-only' />
                </div>
                <div className='flex justify-between'>
                  <span className='text-gray-600'>Gültig bis:</span>
                  <DateTimeDisplay date={mockVoucher.validUntil} format='date-only' />
                </div>
                <div className='flex justify-between'>
                  <span className='text-gray-600'>Kategorie:</span>
                  <Badge variant='outline'>{mockVoucher.category}</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Provider Info */}
            <Card>
              <CardHeader>
                <CardTitle>Anbieter</CardTitle>
              </CardHeader>
              <CardContent className='space-y-3'>
                <div>
                  <h4 className='font-semibold text-gray-800'>{mockVoucher.provider.name}</h4>
                  <p className='text-sm text-gray-600'>{mockVoucher.provider.address}</p>
                </div>

                {mockVoucher.provider.phone && (
                  <div>
                    <span className='text-sm text-gray-600'>Telefon: </span>
                    <a href={`tel:${mockVoucher.provider.phone}`} className='text-sm text-cyan-600 hover:underline'>
                      {mockVoucher.provider.phone}
                    </a>
                  </div>
                )}

                {mockVoucher.provider.website && (
                  <div>
                    <a
                      href={`https://${mockVoucher.provider.website}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-sm text-cyan-600 hover:underline'
                    >
                      {mockVoucher.provider.website}
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
